import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { BookingProvider } from "./context/BookingContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Contact from "./components/Contact";
import SEO from "./components/SEO";
import PromoBanner from "./components/PromoBanner";
import WelcomePopup from "./components/WelcomePopup";

// Pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import LocalServiceLanderPage from "./pages/LocalServiceLanderPage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import PillarPage from "./pages/PillarPage";
import LocationPage from "./pages/LocationPage";
import { PrivacyPolicyPage, TermsOfServicePage } from "./pages/LegalPages";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import Technology from "./components/Technology";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// (Per-route titles are now handled by <SEO> + react-helmet-async on each page.)
function PageTitle() { return null; }

// Public site layout (with navbar/footer/booking modal + promo banner + welcome popup)
function PublicLayout() {
  const [bannerVisible, setBannerVisible] = React.useState(() => {
    try {
      return window.localStorage.getItem('akira_promo_banner_dismissed_v1') !== '1';
    } catch {
      return true;
    }
  });
  return (
    <BookingProvider>
      <div className={`App ${bannerVisible ? 'has-promo-banner' : ''}`}>
        <PromoBanner onDismiss={() => setBannerVisible(false)} />
        <Navbar />
        <Outlet />
        <Footer />
        <WelcomePopup />
      </div>
    </BookingProvider>
  );
}

// Admin layout — no navbar/footer, wrapped in AdminAuthProvider
function AdminLayout() {
  return (
    <AdminAuthProvider>
      <Outlet />
    </AdminAuthProvider>
  );
}

// Redirect /blog/:slug → /articles/:slug while preserving the slug
function RedirectBlogToArticles() {
  const { pathname } = useLocation();
  const tail = pathname.replace(/^\/blog/, '');
  return <Navigate to={`/articles${tail}`} replace />;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageTitle />
        <Routes>
          {/* Admin (no navbar/footer) */}
          <Route element={<AdminLayout />}>
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
          </Route>

          {/* Public site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/botox-alhambra" element={<LocalServiceLanderPage landerSlug="botox-alhambra" />} />
            <Route path="/services/dermal-fillers-alhambra" element={<LocalServiceLanderPage landerSlug="dermal-fillers-alhambra" />} />
            <Route path="/services/botox-san-marino" element={<LocalServiceLanderPage landerSlug="botox-san-marino" />} />
            <Route path="/services/botox-pasadena" element={<LocalServiceLanderPage landerSlug="botox-pasadena" />} />
            <Route path="/services/botox-arcadia" element={<LocalServiceLanderPage landerSlug="botox-arcadia" />} />
            <Route path="/services/dermal-fillers-pasadena" element={<LocalServiceLanderPage landerSlug="dermal-fillers-pasadena" />} />
            <Route path="/services/dermal-fillers-arcadia" element={<LocalServiceLanderPage landerSlug="dermal-fillers-arcadia" />} />
            <Route path="/services/dermal-fillers-san-marino" element={<LocalServiceLanderPage landerSlug="dermal-fillers-san-marino" />} />
            <Route path="/services/:slug" element={<ServiceDetailPage />} />
            <Route path="/technology" element={<Technology />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/articles" element={<BlogPage />} />
            <Route path="/articles/pillar/:pillarSlug" element={<PillarPage />} />
            <Route path="/articles/:slug" element={<BlogPostPage />} />
            {/* Spanish articles */}
            <Route path="/es/articles" element={<BlogPage lang="es" />} />
            <Route path="/es/articles/:slug" element={<BlogPostPage lang="es" />} />
            {/* Back-compat redirects from the old /blog URLs */}
            <Route path="/blog" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/:slug" element={<RedirectBlogToArticles />} />
            <Route path="/contact" element={
            <>
              <SEO
                title="Book a Consultation in Alhambra"
                description="Visit Akira Signature Care at 506 N Garfield Ave Ste 210, Alhambra, CA 91801. Call (626) 737-3183 or book your consultation online."
                path="/contact"
              />
              <div id="contact"><Contact /></div>
            </>
          } />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/locations/:locationSlug" element={<LocationPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
